import { Users, Clock, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

const getTATColor = (tat: string): "success" | "warning" | "destructive" | "secondary" => {
  const hours = parseInt(tat.split("h")[0]);

  if (hours < 2) return "success";
  if (hours >= 2 && hours < 3) return "secondary";
  if (hours >= 3 && hours < 5) return "warning";
  return "destructive";
};

interface RoomCardProps {
  roomName: string;
  patientCount: number;
  avgTAT: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}

export const RoomCard = ({ roomName, patientCount, avgTAT, isExpanded = false, onToggle }: RoomCardProps) => {
  const tatColor = getTATColor(avgTAT);

  return (
    <div
      className="bg-card border border-border rounded-lg p-1.5 sm:p-2 hover:shadow-md transition-shadow cursor-pointer min-h-[60px] sm:min-h-[auto]"
      onClick={onToggle}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: Room name and patient count */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-2 min-w-0">
          <h3 className="text-base sm:text-md font-medium text-foreground truncate">{roomName}</h3>
          <Badge variant="secondary" className="flex items-center space-x-1 rounded-full px-1.5 py-0.5 w-fit shrink-0">
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
            <span className="text-sm sm:text-base font-medium text-primary whitespace-nowrap">{patientCount} Patients</span>
          </Badge>
        </div>

        {/* Right: TAT and chevron */}
        <div className="flex items-center space-x-2 sm:space-x-3 shrink-0">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <Clock
              className={cn(
                "w-4 h-4 sm:w-5 sm:h-5",
                tatColor === "success" && "text-success",
                tatColor === "warning" && "text-warning",
                tatColor === "destructive" && "text-destructive",
                tatColor === "secondary" && "text-muted-foreground",
              )}
            />
            <span className="hidden sm:inline text-sm text-muted-foreground">Avg. TAT:</span>
            <span
              className={cn(
                "text-sm sm:text-base font-medium whitespace-nowrap",
                tatColor === "success" && "text-success",
                tatColor === "warning" && "text-warning",
                tatColor === "destructive" && "text-destructive",
                tatColor === "secondary" && "text-muted-foreground",
              )}
            >
              {avgTAT}
            </span>
          </div>
          <ChevronDown
            className={cn(
              "w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground transition-transform duration-200",
              isExpanded && "rotate-180",
            )}
          />
        </div>
      </div>
    </div>
  );
};
